/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description constants
 */

/**
 * DEFAULT_TIME_ZONE
 * @type {string}
 * @exports
 */
const DEFAULT_TIME_ZONE = 'CST6CDT';

/**
 * MINUTES_IN_HOUR
 */
const MINUTES_IN_HOUR = 60;

/**
 * SHORT_DATE_FORMAT
 */
const SHORT_DATE_FORMAT = 'MMM D, YYYY';

const YEAR_MONTH_DATE_FORMAT = 'YYYY-MM-DD';

const YEAR_MONTH_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE = 'YYYY-MM-DDTHH:mm:ssZ';

const MONTH_DATE_YEAR_TIME_FORMAT = 'MM-DD-YYYY h:mm:ss A';

const MONTH_DATE_YEAR_TIME_WITH_MERIDIEM_FORMAT = 'MMM DD, YYYY, h:mma';

const YEAR_MONTH_DATE_TIME_TIMEZONE_FORMAT = 'YYYY-MM-DD HH:mm:ss Z';

/**
 * MINIMUM_DESKTOP_VIEWPORT_WIDTH
 * for mobile load blocking
 */
const MINIMUM_DESKTOP_VIEWPORT_WIDTH = 640;

/**
 * MONTH_DATE_YEAR_
 */
const MONTH_DATE_YEAR_SLASH_FORMAT = 'MM/DD/YYYY';

// variations of MONTH_DATE_YEAR format
const MONTH_DATE_YEAR_SLASH_FORMATS = [MONTH_DATE_YEAR_SLASH_FORMAT, 'M/D/YYYY', 'M/D/YY', 'MM/DD/YY'];

const MONTH_DATE_YEAR_TIME_SLASH_FORMAT = 'MM/DD/YYYY h:mm:ss A';

/**
 * MONTH_YEAR_FORMAT
 */
const MONTH_YEAR_FORMAT = 'MMM D, YYYY';

/**
 * empty value for details in cards
 */
const EMPTY_VALUE = '-';

/**
 * empty value for date range
 */
const EMPTY_VALUE_FOR_DATE_RANGE = '--/--/----';

/**
 * PHONE_REGEX 10 digit
 */
const PHONE_REGEX = /^[0-9]{10}$/;

/**
 * form layout style
 */
// TODO - remove this
const FORM_LAYOUT_STYLE = { width: '80%', margin: 'auto' };

/**
 * Form field patterns
 */
const FIELD_VALIDATORS = {
	patientFirstName: {
		pattern: new RegExp("^[a-zA-Z0-9\\s!&,()+'-./;?=#\\]]{1,35}$"),
		message: 'Please enter a valid first name',
	},
	patientMiddleName: {
		pattern: new RegExp("^[a-zA-Z0-9\\s!&,()+'-./;?=#\\]]{1,25}$"),
		message: 'Please enter a valid middle name',
	},
	patientLastName: {
		pattern: new RegExp("^[a-zA-Z0-9\\s!&,()+'-./;?=#\\]]{1,60}$"),
		message: 'Please enter a valid last name',
	},
	patientSuffix: {
		pattern: new RegExp("^[a-zA-Z0-9\\s!&,()+'-./;?=#\\]]{1,10}$"),
		message: 'Please enter a valid suffix',
	},
	stateCode: {
		pattern: new RegExp('^[A-Z]{2,2}$'),
		message: 'Please enter a valid state code',
	},
	ssn: {
		pattern: new RegExp('^\\d{9}$'),
		message: 'Please enter a valid Social Security Number',
	},
	memberId: {
		pattern: new RegExp('^[A-Za-z0-9-]{2,80}$'),
		message: 'Please enter a valid Member ID',
	},
	commonIdentifier: {
		pattern: new RegExp('^[^~:^]{1,50}$'),
		message: 'Please enter a valid Common Identifier',
	},
	npi: {
		pattern: new RegExp('^\\d{10}$'),
		message: 'Please enter a valid NPI Number',
	},
	organizationName: {
		pattern: new RegExp('^[0-9a-zA-Z \\-_]+$'),
		message: 'Please enter a valid Organization Name',
	},
	providerName: {
		pattern: new RegExp('^[0-9a-zA-Z \\-_]+$'),
		message: 'Please enter a valid Provider Name',
	},
	locationName: {
		pattern: new RegExp('^[0-9a-zA-Z \\-_]+$'),
		message: 'Please enter a valid Location Name',
	},
	tin: {
		pattern: new RegExp('^\\d{9}$'),
		message: 'Please enter a 9-digit TIN/EIN',
	},
};

// labels for the sort column
const SORT_COLUMN_LABELS = {
	oldToNew: 'Old to New',
	newToOld: 'New to Old',
	lowToHigh: 'Low to High',
	highToLow: 'High to Low',
};

// animation
const DEFAULT_SLIDE_IN_ANIMATION = 'slideIn 0.3s ease-in-out';

const DEFAULT_SLIDE_OUT_ANIMATION = 'slideOut 0.3s ease-in-out';

/**
 * posthog groups
 * Don't change the mapping as it will break the posthog groups or create new groups
 */
const POSTHOG_GROUPS = {
	tenant: 'tenant',
};

/**
 * PLATFORMS for app
 */
const PLATFORMS: Record<string, UniversalSearch.Platform> = {
	mac: 'mac',
	other: 'other',
};

/**
 * HOT_KEYS for app
 */
const HOT_KEYS = {
	universalSearch: {
		[PLATFORMS.mac]: {
			key: 'meta+k',
			icon: '⌘ K',
		},
		[PLATFORMS.other]: {
			key: 'ctrl+k',
			icon: 'Ctrl K',
		},
	},
};

const DEFAULT_PAGINATION = {
	pageSize: 20,
	pageNumber: 1,
};

// fhir entity types
const FHIR_ENTITY_TYPES: Record<string, EntityTypeEnum> = {
	paymentReconciliation: 'PaymentReconciliation',
	claim: 'Claim',
};

// amount stat card styles
const AMOUNT_STAT_CARD_STYLES = {
	default: {
		containerStyle: {
			borderColor: '#F0F0F0',
			backgroundColor: '#fafafa',
		},
		iconStyle: {
			backgroundColor: '#d9d9d9',
		},
		valueStyle: {
			color: '#232835',
		},
	},
	green: {
		containerStyle: {
			borderColor: '#95DE64',
			backgroundColor: '#F6FFED',
		},
		iconStyle: {
			backgroundColor: '#95DE64',
		},
		valueStyle: {
			color: '#52C41A',
		},
	},
	orange: {
		containerStyle: {
			borderColor: '#FFC069',
			backgroundColor: '#FFF7E6',
		},
		iconStyle: {
			backgroundColor: '#FFC069',
		},
		valueStyle: {
			color: '#FA8C16',
		},
	},
	blue: {
		containerStyle: {
			borderColor: '#A2C2F2',
			backgroundColor: '#F0F7FF',
		},
		iconStyle: {
			backgroundColor: '#4C7BD9',
		},
		valueStyle: {
			color: '#4C7BD9',
		},
	},
	red: {
		containerStyle: {
			borderColor: '#ffa39e',
			backgroundColor: '#fff1f0',
		},
		iconStyle: {
			backgroundColor: '#ffa39e',
		},
		valueStyle: {
			color: '#cf1322',
		},
	},
};

// customer types for veritable app
const CUSTOMER_TYPE: Record<App.Customer, App.Customer> = {
	eligibility: 'eligibility',
	billing: 'billing',
};

const ARCHETYPE_1: App.Archetype = 'archetype1';

const ARCHETYPE_2: App.Archetype = 'archetype2';

// customer archetype veritable app
const CUSTOMER_ARCHETYPE = {
	archetype1: ARCHETYPE_1,
	archetype2: ARCHETYPE_2,
};

// export entity types
const EXPORT_ENTITY_TYPES: Record<string, StatementEntityType> = {
	patient: 'patient',
	guarantor: 'guarantor',
};

const NO_ACCESS_TOOLTIP_TEXT = 'Contact your Super Admin to access this feature.';

const S3_BLOB_CATEGORY: Record<S3BlobCategory, S3BlobCategory> = {
	entityattachment: 'entityattachment',
	batchupload: 'batchupload',
	ingestionupload: 'ingestionupload',
};

export {
	NO_ACCESS_TOOLTIP_TEXT,
	DEFAULT_TIME_ZONE,
	SHORT_DATE_FORMAT,
	YEAR_MONTH_DATE_FORMAT,
	MONTH_DATE_YEAR_SLASH_FORMAT,
	MONTH_DATE_YEAR_SLASH_FORMATS,
	EMPTY_VALUE,
	EMPTY_VALUE_FOR_DATE_RANGE,
	YEAR_MONTH_DATE_TIME_FORMAT,
	MONTH_DATE_YEAR_TIME_FORMAT,
	YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE,
	PHONE_REGEX,
	DEFAULT_SLIDE_IN_ANIMATION,
	DEFAULT_SLIDE_OUT_ANIMATION,
	POSTHOG_GROUPS,
	FIELD_VALIDATORS,
	SORT_COLUMN_LABELS,
	MINIMUM_DESKTOP_VIEWPORT_WIDTH,
	PLATFORMS,
	HOT_KEYS,
	MINUTES_IN_HOUR,
	MONTH_YEAR_FORMAT,
	FORM_LAYOUT_STYLE,
	MONTH_DATE_YEAR_TIME_WITH_MERIDIEM_FORMAT,
	DEFAULT_PAGINATION,
	FHIR_ENTITY_TYPES,
	YEAR_MONTH_DATE_TIME_TIMEZONE_FORMAT,
	MONTH_DATE_YEAR_TIME_SLASH_FORMAT,
	AMOUNT_STAT_CARD_STYLES,
	CUSTOMER_TYPE,
	EXPORT_ENTITY_TYPES,
	S3_BLOB_CATEGORY,
	CUSTOMER_ARCHETYPE,
};
